import { z } from 'zod'
import { RESERVED_KEYS } from './contract.js'
import type { Definitie } from './definitie.js'
import { Block, FieldId } from './id.js'
import { Text } from './text.js'
import type { RowField, Field } from './field.js'

/**
 * Hoe een veld uit een eerder blok eruitzag voor wie het invulde (R-54, R-55, R-100, R-143).
 *
 * **Waarom dit bestaat.** Een verwijzing krijgt uit het blokdocument alleen de kale waarde
 * (`bouwBlokdocument`): `openbaar` in plaats van "Ja, openbaar beschikbare data", `650000` zonder
 * valuta, de sleutels van een rij in plaats van de kolomkoppen. Het contract van het blok zegt sinds
 * 31 oktober 2026 van een bedrag dat het een bedrag in euro is (`fillCompartimentFields` in
 * `contractGenerator.ts`, R-54), maar niet hoe het veld heette of welke keuzes het had, en de pod van
 * de beoordeling kent de definitie van de aanvraag niet. Wat de beoordelaar hoort te zien staat
 * alleen in de definitie waarmee de aanvrager invulde, en dit is het deel daarvan dat een lezer
 * nodig heeft: het label, de soort, de keuzes met hun label en de kolommen van een herhaalgroep.
 *
 * **Elke tekst in beide talen**, zoals in de definitie (R-143): de taalwissel van de lezer kiest,
 * niet het moment van bevriezen.
 *
 * **Geen contractvorm, en daarom niet in het blokdocument.** Het blokdocument is wat een blok aan
 * een volgend blok en aan het processysteem belooft (D-37) en gaat naar buiten; hoe een veld er in
 * het formulier uitzag is presentatie van dit product en hoort daar niet bij.
 */
const TextView = z.strictObject({ soort: z.literal('tekst'), label: Text })
const NumberView = z.strictObject({ soort: z.literal('getal'), label: Text })
/**
 * Een bedrag, met zijn valuta. Eén valuta, want het bedragveld kent er één: het teken staat vast
 * in de control (`Getal.tsx`) en de definitie heeft er geen instelling voor. De valuta staat hier
 * toch uitgeschreven, zodat een lezer haar niet hoeft aan te nemen.
 */
const AmountView = z.strictObject({ soort: z.literal('bedrag'), label: Text, valuta: z.literal('EUR') })
const DateView = z.strictObject({ soort: z.literal('datum'), label: Text })
const TimestampView = z.strictObject({ soort: z.literal('tijdstip'), label: Text })
const ChoiceView = z.strictObject({
  soort: z.literal('keuze'),
  label: Text,
  opties: z.array(z.strictObject({ waarde: z.string().min(1), label: Text })).min(1),
})

/** De weergave van één waarde: een veld buiten een herhaalgroep, of één kolom erin. */
export const SingleView = z.discriminatedUnion('soort', [
  TextView,
  NumberView,
  AmountView,
  DateView,
  TimestampView,
  ChoiceView,
])
export type SingleView = z.infer<typeof SingleView>

/**
 * Een herhaalgroep: de kolommen in de orde van de definitie, elk met het id van het rijveld (de
 * sleutel in een rij van het blokdocument) en de weergave van dat rijveld.
 */
const TableView = z.strictObject({
  soort: z.literal('tabel'),
  label: Text,
  kolommen: z.array(z.strictObject({ id: FieldId, weergave: SingleView })).min(1),
})

export const FieldView = z.discriminatedUnion('soort', [
  TextView,
  NumberView,
  AmountView,
  DateView,
  TimestampView,
  ChoiceView,
  TableView,
])
export type FieldView = z.infer<typeof FieldView>

/** De weergave van de velden van één stand, per pad in het blokdocument. */
export const Views = z.record(z.string().min(1), FieldView)
export type Views = z.infer<typeof Views>

/**
 * De weergave per bronveld, bevroren op een instantie bij het aanmaken (D-36, R-100, R-201).
 *
 * Per blok, per stand, per pad in het blokdocument. **Per stand en niet per blok**, en dat is het
 * randgeval van R-102: een volgend uitvraagmoment is een nieuwe publicatie van hetzelfde blok, met
 * mogelijk andere labels of keuzes (D-34). Twee standen van één blok kunnen dus onder twee
 * publicaties zijn ingevuld, en de weergave van een stand is die van haar eigen publicatie. De
 * sleutel is de `$stand` van het opgeloste document (D-72), dus de revisie.
 *
 * Een stand van een blok buiten dit product heeft geen definitie en dus geen weergave: hij staat
 * er niet in, en de lezer toont dan wat er altijd stond.
 *
 * **De vorm mag groeien.** Een pod leest haar verdraagzaam, veld voor veld: wat hij niet kent (een
 * nieuwe soort, een sleutel erbij, een derde taal) laat hij weg, en dat veld toont dan de kale
 * waarde (`lenient` in `apps/runtime/src/storeclient.ts`). Een bevroren pod valt dus niet om
 * op een weergave die na zijn publicatie is uitgebreid (R-101, D-36).
 */
export const SourceView = z.record(Block, z.record(z.string().min(1), Views))
export type SourceView = z.infer<typeof SourceView>

/**
 * De weergave van elk veld dat in een compartiment van het blokdocument landt, op zijn pad
 * (`compartimenten.<compartiment>.<veld-id>`, D-69).
 *
 * **Precies de velden die `bouwBlokdocument` in een compartiment zet**, want alleen daar kan een
 * verwijzing uit een volgend blok naar wijzen (D-38). De vaste componenten en de criteria staan in
 * het gestructureerde deel; hun vorm beschrijft het contract (D-73) en ze krijgen hier niets.
 * Welk compartiment naar welk blok doorgaat (D-70) staat in de contractregistry, die dit model
 * niet kent; een weergave van een veld dat niet doorgaat is onschadelijk, want zonder waarde in
 * het document toont geen lezer haar.
 */
export function fieldViews(definitie: Definitie): Views {
  const out: Record<string, FieldView> = {}
  for (const section of definitie.secties) {
    for (const field of section.velden) {
      const compartiment = 'compartiment' in field ? field.compartiment : undefined
      if (compartiment === undefined) continue
      const view = fieldView(field)
      if (view !== undefined) out[`${RESERVED_KEYS[0]}.${compartiment}.${field.id}`] = view
    }
  }
  // Door het eigen schema voordat het de deur uit gaat, zoals `bouwBlokdocument`.
  return Views.parse(out)
}

function fieldView(field: Field): FieldView | undefined {
  if (field.control === 'herhaalgroep') {
    return {
      soort: 'tabel',
      label: field.label,
      kolommen: field.rijVelden.map((rowField) => ({ id: rowField.id, weergave: singleView(rowField) })),
    }
  }
  // Een criterium, een verwijzing en een component landen niet in een compartiment
  // (`needsCompartiment`); deze tak is er voor de volledigheid van het type.
  if (field.control === 'criterium' || field.control === 'verwijzing' || field.control === 'component') {
    return undefined
  }
  // Het akkoord per persoon is in een compartiment één ja of nee, compleet of niet (eisenboek 12.56).
  if (field.control === 'akkoordPerPersoon') return singleView({ id: field.id, label: field.label, control: 'toggle' })
  return singleView(field)
}

/**
 * De soort uit het controltype. Wat geen getal, bedrag, datum of keuze is, is tekst: daar maakt de
 * lezer al het juiste van uit de vorm van de waarde (een lijst trefwoorden, Ja of Nee, bijlagen).
 */
function singleView(field: RowField): SingleView {
  const label = field.label
  switch (field.control) {
    case 'currency':
      return { soort: 'bedrag', label, valuta: 'EUR' }
    case 'number':
    case 'slider':
    case 'rating':
      return { soort: 'getal', label }
    case 'date':
      return { soort: 'datum', label }
    case 'datetime':
      return { soort: 'tijdstip', label }
    case 'dropdown':
    case 'multiselect':
    case 'radio':
      return { soort: 'keuze', label, opties: field.opties.map(({ waarde: value, label: option }) => ({ waarde: value, label: option })) }
    case 'checkbox':
      // Zonder opties is een checkbox één losse instemming, en dat is Ja of Nee.
      return field.opties === undefined
        ? { soort: 'tekst', label }
        : { soort: 'keuze', label, opties: field.opties.map(({ waarde: value, label: option }) => ({ waarde: value, label: option })) }
    default:
      return { soort: 'tekst', label }
  }
}
